import { Clock, Loader2, Save, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { LegacyGenerationJob } from "~/entities/chat";
import { useRuntimeConfig } from "~/entities/system";
import { useElapsedTimer } from "~/shared/hooks/useElapsedTimer";
import { cn } from "~/shared/lib/utils";

interface MessageProgressProps {
    job: LegacyGenerationJob;
    onCancel?: () => void;
    isCancelling?: boolean;
}

const VIDEO_GENERATION_MODES = new Set([
    "text_to_video",
    "image_to_video",
    "frames",
    "motion_control",
    "upscale_video",
]);

function isVideoMode(generationMode: string | undefined): boolean {
    return generationMode != null && VIDEO_GENERATION_MODES.has(generationMode);
}

// queued — синяя карточка с пульсирующими часами + Cancel.
// in_progress — фиолетовая карточка с determinate-баром (заполняется по таймауту) + Stop.
// recovering — янтарная карточка: результат уже есть, заливаем в наше хранилище;
// cancel здесь не показываем (см. план — нельзя отменять «сохранение»).
// Имя провайдера НЕ показываем (правило).
export function MessageProgress({ job, onCancel, isCancelling = false }: MessageProgressProps) {
    const { t } = useTranslation("chat");
    const isActive = job.status === "queued" || job.status === "in_progress" || job.status === "recovering";

    const runtimeConfig = useRuntimeConfig();
    const generationMode = job.requestPayload?.generationMode;
    const timeoutMinutes = isVideoMode(generationMode)
        ? runtimeConfig.data?.staleJobsVideoTimeoutMinutes
        : runtimeConfig.data?.staleJobsImageTimeoutMinutes;
    const timeoutSec = timeoutMinutes != null ? timeoutMinutes * 60 : null;

    const { formatted, progressPct, isOverdue } = useElapsedTimer(job.createdAt, isActive, null, timeoutSec);

    if (job.status === "queued") {
        return (
            <div
                role="status"
                aria-live="polite"
                className="mt-3 flex items-center gap-3 rounded-lg border border-blue-500/10 bg-blue-500/[0.06] p-3"
            >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500/10">
                    <Clock className="h-4 w-4 animate-pulse text-blue-400" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-blue-300">
                            {t("messages.progress.queued")}
                        </span>
                        <span
                            className={cn(
                                "font-mono text-xs",
                                isOverdue ? "text-red-400" : "text-blue-400/60",
                            )}
                        >
                            {formatted}
                        </span>
                    </div>
                    <div className="mt-0.5 text-xs text-blue-400/70">{t("messages.progress.queuedHint")}</div>
                </div>
                {onCancel && (
                    <CancelButton
                        onClick={onCancel}
                        disabled={isCancelling}
                        label={t("messages.progress.cancelButton")}
                        colorClasses="text-blue-400/70 hover:bg-blue-500/10 hover:text-blue-300"
                    />
                )}
            </div>
        );
    }

    if (job.status === "in_progress") {
        return (
            <div
                role="status"
                aria-live="polite"
                className="mt-3 rounded-lg border border-violet-500/10 bg-violet-500/[0.06] p-3"
            >
                <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-500/10">
                        <Loader2 className="h-4 w-4 animate-spin text-violet-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-violet-300">
                                {t("messages.progress.rendering")}
                            </span>
                            <span
                                className={cn(
                                    "font-mono text-xs",
                                    isOverdue ? "text-red-400" : "text-violet-400/60",
                                )}
                            >
                                {formatted}
                            </span>
                        </div>
                        <div className="mt-0.5 text-xs text-violet-400/70">
                            {t("messages.progress.renderingHint")}
                        </div>
                    </div>
                    {onCancel && (
                        <CancelButton
                            onClick={onCancel}
                            disabled={isCancelling}
                            label={t("messages.progress.stopButton")}
                            colorClasses="text-violet-400/70 hover:bg-violet-500/10 hover:text-violet-300"
                        />
                    )}
                </div>
                <div className="relative mt-3 h-1 overflow-hidden rounded-full bg-white/5">
                    <div
                        className={cn(
                            "absolute inset-y-0 left-0 rounded-full transition-[width] duration-1000 ease-linear",
                            isOverdue ? "bg-red-500/70" : "bg-gradient-to-r from-violet-500 to-blue-500",
                        )}
                        style={{ width: `${progressPct}%` }}
                    />
                </div>
            </div>
        );
    }

    if (job.status === "recovering") {
        return (
            <div
                role="status"
                aria-live="polite"
                className="mt-3 flex items-center gap-3 rounded-lg border border-amber-500/15 bg-amber-500/[0.06] p-3"
            >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
                    <Save className="h-4 w-4 animate-pulse text-amber-400" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-amber-200">
                            {t("messages.progress.recovering")}
                        </span>
                        <span className="font-mono text-xs text-amber-400/60">{formatted}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-amber-400/80">
                        {t("messages.progress.recoveringHint")}
                    </div>
                </div>
            </div>
        );
    }

    return null;
}

interface CancelButtonProps {
    onClick: () => void;
    disabled: boolean;
    label: string;
    colorClasses: string;
}

function CancelButton({ onClick, disabled, label, colorClasses }: CancelButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={cn(
                "inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors",
                colorClasses,
                disabled && "cursor-not-allowed opacity-50",
            )}
        >
            <X className="h-3 w-3" />
            {label}
        </button>
    );
}
